import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, Download, Search, Package, TrendingUp, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierOrderList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [orderListData, setOrderListData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const printRef = useRef(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error("Failed to load suppliers");
    }
  };

  const fetchOrderList = async (supplierId) => {
    if (!supplierId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/order-list`);
      setOrderListData(response.data);
    } catch (error) {
      toast.error("Failed to load order list");
      setOrderListData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (value) => {
    setSelectedSupplierId(value);
    setSearchFilter("");
    fetchOrderList(value);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Supplier Order List - ${orderListData?.supplier?.name || ""}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .header h1 { font-size: 18px; margin-bottom: 5px; }
          .header .supplier-info { font-size: 12px; color: #555; }
          .period-info { display: flex; justify-content: space-around; margin-bottom: 15px; font-size: 10px; background: #f5f5f5; padding: 8px; border-radius: 4px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #333; color: white; font-size: 10px; text-transform: uppercase; }
          td { font-size: 10px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-mono { font-family: monospace; }
          .highlight { background: #ffffd0; }
          .low-stock { background: #ffe0e0; }
          .footer { margin-top: 15px; font-size: 9px; color: #777; text-align: center; }
          tr:nth-child(even) { background: #fafafa; }
          @media print {
            body { padding: 10px; }
            .header { page-break-after: avoid; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExportCSV = () => {
    if (!orderListData?.products?.length) return;

    const headers = ["PLU Code", "Supplier PLU", "Product Name", "Current Stock", "Min Stock", "Max Stock", "Cost Price", "Selling Price", "Qty This Month", "Qty YTD", "Qty Last Year"];
    const rows = filteredProducts.map(p => [
      p.plu_code,
      p.supplier_plu,
      p.name,
      p.current_stock,
      p.min_stock,
      p.max_stock,
      p.cost_price?.toFixed(2),
      p.selling_price?.toFixed(2),
      p.qty_this_month,
      p.qty_ytd,
      p.qty_last_year
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ""}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Order_List_${orderListData.supplier?.name?.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  // Filter products based on search
  const filteredProducts = orderListData?.products?.filter(p => 
    !searchFilter || 
    p.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    p.plu_code?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    p.supplier_plu?.toLowerCase().includes(searchFilter.toLowerCase())
  ) || [];

  // Calculate totals
  const totals = filteredProducts.reduce((acc, p) => ({
    thisMonth: acc.thisMonth + (p.qty_this_month || 0),
    ytd: acc.ytd + (p.qty_ytd || 0),
    lastYear: acc.lastYear + (p.qty_last_year || 0)
  }), { thisMonth: 0, ytd: 0, lastYear: 0 });

  return (
    <div className="p-6 space-y-6" data-testid="supplier-order-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Supplier Order List</h1>
          <p className="text-slate-500 text-sm">View products by supplier with sales history for ordering</p>
        </div>
      </div>

      {/* Supplier Selection */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-md">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Select Supplier</label>
              <Select value={selectedSupplierId} onValueChange={handleSupplierChange}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Choose a supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name} ({supplier.supplier_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {orderListData && (
              <>
                <div className="flex-1 max-w-xs">
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Filter Products</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search PLU or name..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                </div>

                <Button variant="outline" onClick={() => fetchOrderList(selectedSupplierId)} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>

                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>

                <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                  <Printer className="h-4 w-4 mr-2" />
                  Print List
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400 mb-3" />
            <p className="text-slate-500">Loading order list...</p>
          </CardContent>
        </Card>
      ) : orderListData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{filteredProducts.length}</div>
                    <div className="text-xs text-slate-500">Products</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{totals.thisMonth.toFixed(0)}</div>
                    <div className="text-xs text-slate-500">Qty This Month</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{totals.ytd.toFixed(0)}</div>
                    <div className="text-xs text-slate-500">Qty YTD</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{totals.lastYear.toFixed(0)}</div>
                    <div className="text-xs text-slate-500">Qty Last Year</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800">
                      <TableHead className="text-white font-semibold">PLU Code</TableHead>
                      <TableHead className="text-white font-semibold">Supplier PLU</TableHead>
                      <TableHead className="text-white font-semibold">Product Name</TableHead>
                      <TableHead className="text-white font-semibold text-right">Stock</TableHead>
                      <TableHead className="text-white font-semibold text-right">Min</TableHead>
                      <TableHead className="text-white font-semibold text-right">Max</TableHead>
                      <TableHead className="text-white font-semibold text-right">Cost</TableHead>
                      <TableHead className="text-white font-semibold text-right">Sell</TableHead>
                      <TableHead className="text-white font-semibold text-center bg-green-700">This Month</TableHead>
                      <TableHead className="text-white font-semibold text-center bg-purple-700">YTD</TableHead>
                      <TableHead className="text-white font-semibold text-center bg-orange-700">Last Year</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                          No products found for this supplier
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProducts.map((product, index) => {
                        const isLowStock = product.current_stock <= product.min_stock;
                        return (
                          <TableRow 
                            key={product.id} 
                            className={`${index % 2 === 0 ? "bg-white" : "bg-slate-50"} ${isLowStock ? "!bg-red-50" : ""}`}
                          >
                            <TableCell className="font-mono font-medium">{product.plu_code}</TableCell>
                            <TableCell className="font-mono text-slate-600">{product.supplier_plu || "-"}</TableCell>
                            <TableCell className="font-medium max-w-xs truncate">{product.name}</TableCell>
                            <TableCell className={`text-right font-mono ${isLowStock ? "text-red-600 font-bold" : ""}`}>
                              {product.current_stock}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-500">{product.min_stock}</TableCell>
                            <TableCell className="text-right font-mono text-slate-500">{product.max_stock}</TableCell>
                            <TableCell className="text-right font-mono">R {product.cost_price?.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-mono">R {product.selling_price?.toFixed(2)}</TableCell>
                            <TableCell className="text-center font-mono font-semibold bg-green-50">{product.qty_this_month}</TableCell>
                            <TableCell className="text-center font-mono font-semibold bg-purple-50">{product.qty_ytd}</TableCell>
                            <TableCell className="text-center font-mono font-semibold bg-orange-50">{product.qty_last_year}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Printable Content (Hidden) */}
          <div className="hidden">
            <div ref={printRef}>
              <div className="header">
                <h1>SUPPLIER ORDER LIST</h1>
                <div className="supplier-info">
                  <strong>{orderListData.supplier?.name}</strong> ({orderListData.supplier?.supplier_number})<br />
                  {orderListData.supplier?.contact_person && `Contact: ${orderListData.supplier.contact_person} | `}
                  {orderListData.supplier?.phone && `Tel: ${orderListData.supplier.phone} | `}
                  {orderListData.supplier?.email && `Email: ${orderListData.supplier.email}`}
                </div>
              </div>
              
              <div className="period-info">
                <span><strong>This Month:</strong> {orderListData.period_info?.this_month}</span>
                <span><strong>YTD:</strong> {orderListData.period_info?.ytd}</span>
                <span><strong>Last Year:</strong> {orderListData.period_info?.last_year}</span>
                <span><strong>Products:</strong> {filteredProducts.length}</span>
              </div>

              <table>
                <thead>
                  <tr>
                    <th>PLU Code</th>
                    <th>Supp PLU</th>
                    <th>Product Name</th>
                    <th className="text-right">Stock</th>
                    <th className="text-right">Min</th>
                    <th className="text-right">Max</th>
                    <th className="text-right">Cost</th>
                    <th className="text-center">This Mth</th>
                    <th className="text-center">YTD</th>
                    <th className="text-center">Last Yr</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => {
                    const isLowStock = product.current_stock <= product.min_stock;
                    return (
                      <tr key={product.id} className={isLowStock ? "low-stock" : ""}>
                        <td className="font-mono">{product.plu_code}</td>
                        <td className="font-mono">{product.supplier_plu || "-"}</td>
                        <td>{product.name}</td>
                        <td className="text-right font-mono">{product.current_stock}</td>
                        <td className="text-right font-mono">{product.min_stock}</td>
                        <td className="text-right font-mono">{product.max_stock}</td>
                        <td className="text-right font-mono">R {product.cost_price?.toFixed(2)}</td>
                        <td className="text-center font-mono">{product.qty_this_month}</td>
                        <td className="text-center font-mono">{product.qty_ytd}</td>
                        <td className="text-center font-mono">{product.qty_last_year}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: "bold", backgroundColor: "#eee" }}>
                    <td colSpan="7" className="text-right">TOTALS:</td>
                    <td className="text-center">{totals.thisMonth.toFixed(0)}</td>
                    <td className="text-center">{totals.ytd.toFixed(0)}</td>
                    <td className="text-center">{totals.lastYear.toFixed(0)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="footer">
                Generated: {new Date().toLocaleString()} | Low stock items highlighted in red
              </div>
            </div>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Select a supplier to view their product order list</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupplierOrderList;
